import { LogOut, Menu, Sparkles, X } from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';

import { BandeauEchecs } from '@/components/BandeauEchecs';
import { useModulesMasques } from '@/lib/menu-api';
import { type AppModule, GROUPES, MODULES } from '@/lib/modules';
import { cn } from '@/lib/utils';
import { useAuth } from '@/modules/auth/AuthContext';

// Pictogramme toit du favicon, repris comme pastille de marque.
function Toit({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true" className={className}>
      <path
        d="M6 18 L16 8 L26 18 L23 18 L23 26 L19 26 L19 20 L13 20 L13 26 L9 26 L9 18 Z"
        fill="currentColor"
      />
    </svg>
  );
}

function Pastille({ taille }: { taille: 'sm' | 'md' }) {
  return (
    <div
      className={cn(
        'bg-primary text-primary-foreground flex shrink-0 items-center justify-center rounded',
        taille === 'md' ? 'h-9 w-9' : 'h-8 w-8',
      )}
    >
      <Toit className={taille === 'md' ? 'h-6 w-6' : 'h-5 w-5'} />
    </div>
  );
}

// Coquille de l'application, direction « Plein jour ».
//   Ordinateur : menu sombre à gauche, rangé par familles d'usage.
//   Téléphone  : barre sombre en haut, barre d'onglets en bas (les modules du
//                terrain), et un menu plein écran pour tout le reste.
export function AppLayout() {
  const { profil, signOut } = useAuth();
  const location = useLocation();
  const [menuOuvert, setMenuOuvert] = useState(false);
  const isAdmin = profil?.isAdmin ?? false;
  const allowed = (id: string): boolean => {
    // 'relances' lit devis_externes, dont la RLS est réservée aux admin/gérants :
    // l'afficher à quelqu'un d'autre ne donnerait qu'un écran vide.
    if (id === 'utilisateurs' || id === 'personnalisation' || id === 'relances') return isAdmin;
    return isAdmin || id === 'dashboard' || (profil?.modules.includes(id) ?? false);
  };
  const masques = useModulesMasques();
  const visibleModules = MODULES.filter((m) => allowed(m.id));
  // Le menu ne montre ni les écrans qu'on atteint autrement (alertes,
  // assistant), ni ceux que l'administrateur a masqués pour l'entreprise.
  // Masquer n'interdit rien : l'écran reste ouvert à qui a le droit et le lien.
  const dansMenu = visibleModules.filter((m) => !m.horsMenu && !masques.includes(m.id));
  const assistant = visibleModules.some((m) => m.id === 'assistant');
  const current = MODULES.find((m) =>
    m.path === '/' ? location.pathname === '/' : location.pathname.startsWith(m.path),
  );
  const blocked = profil != null && current != null && !allowed(current.id);
  // Quatre onglets au plus, dans l'ordre du menu : le cinquième est « Menu ».
  const onglets = dansMenu.filter((m) => m.onglet).slice(0, 4);
  const initiales = profil
    ? `${profil.prenom.charAt(0)}${profil.nom.charAt(0)}`.toUpperCase()
    : '—';
  const roles =
    profil && profil.roles.length > 0 ? profil.roles.join(', ') : (profil?.email ?? '');

  const lienMenu = (module: AppModule, sombre: boolean) => {
    const Icon = module.icon;
    return (
      <NavLink
        key={module.id}
        to={module.path}
        end={module.path === '/'}
        onClick={() => {
          setMenuOuvert(false);
        }}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3.5 rounded px-4 text-[15px] font-semibold transition-colors',
            sombre ? 'h-14' : 'h-11',
            isActive
              ? 'bg-action text-action-foreground'
              : 'text-sidebar-foreground hover:bg-sidebar-hover hover:text-white',
          )
        }
      >
        <Icon className="h-[22px] w-[22px] shrink-0" strokeWidth={2} />
        <span className="flex-1">{module.label}</span>
        {!module.ready && (
          <span className="bg-sidebar-muted text-sidebar rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase">
            bientôt
          </span>
        )}
      </NavLink>
    );
  };

  // Le menu, groupe par groupe : réutilisé tel quel à gauche sur ordinateur et
  // dans le tiroir plein écran du téléphone.
  const listeMenu = (sombre: boolean) =>
    GROUPES.map((g) => {
      const liste = dansMenu.filter((m) => m.groupe === g.id);
      if (liste.length === 0) return null;
      return (
        <div key={g.id} className="flex flex-col">
          <div className="text-sidebar-muted px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[0.08em]">
            {g.label}
          </div>
          {liste.map((m) => lienMenu(m, sombre))}
        </div>
      );
    });

  // L'assistant n'est plus une entrée de menu : un bouton en haut de l'écran,
  // à portée de main d'où que l'on soit.
  const boutonAssistant = (sombre: boolean) =>
    assistant ? (
      <Link
        to="/assistant"
        title="Assistant"
        aria-label="Assistant"
        className={cn(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded',
          location.pathname.startsWith('/assistant')
            ? 'bg-action text-action-foreground'
            : sombre
              ? 'text-white hover:bg-sidebar-hover'
              : 'text-sidebar-foreground hover:bg-sidebar-hover hover:text-white',
        )}
      >
        <Sparkles className="h-[22px] w-[22px]" />
      </Link>
    ) : null;

  const piedUtilisateur = (
    <div className="border-sidebar-border flex items-center gap-3 border-t px-4 py-3">
      <div className="bg-primary text-primary-foreground flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold">
        {initiales}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white">
          {profil ? `${profil.prenom} ${profil.nom}` : '—'}
        </p>
        <p className="text-sidebar-muted truncate text-xs">{roles}</p>
      </div>
      <button
        type="button"
        title="Déconnexion"
        aria-label="Déconnexion"
        className="text-sidebar-foreground hover:bg-sidebar-hover flex h-11 w-11 shrink-0 items-center justify-center rounded hover:text-white"
        onClick={() => {
          void signOut();
        }}
      >
        <LogOut className="h-[22px] w-[22px]" />
      </button>
    </div>
  );

  return (
    <div className="bg-background flex h-screen overflow-hidden">
      {/* ----- Ordinateur : menu à gauche ----- */}
      <aside className="bg-sidebar text-sidebar-foreground hidden w-60 shrink-0 flex-col lg:flex">
        <div className="border-sidebar-border flex h-16 items-center gap-2.5 border-b pl-4 pr-2">
          <Pastille taille="md" />
          <div className="min-w-0 flex-1">
            <p className="font-display whitespace-nowrap text-[15px] font-bold leading-tight text-white">
              IMP TOITURE
            </p>
            <p className="text-sidebar-muted text-xs leading-tight">CRM</p>
          </div>
          {boutonAssistant(false)}
        </div>
        <nav className="flex-1 overflow-auto p-2">{listeMenu(false)}</nav>
        {piedUtilisateur}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* ----- Téléphone : barre du haut ----- */}
        <header className="bg-sidebar flex h-14 shrink-0 items-center gap-3 px-4 text-white lg:hidden">
          <Pastille taille="sm" />
          <span className="font-display min-w-0 flex-1 truncate text-lg font-bold">
            {current?.label ?? 'IMP TOITURE CRM'}
          </span>
          {boutonAssistant(true)}
        </header>

        <main className="flex-1 overflow-auto">
          <div className="mx-auto max-w-[1200px] p-4 sm:p-6 lg:p-8">
            {/* Au-dessus de chaque écran : ce qui n'a pas pu se charger. */}
            <BandeauEchecs />
            {blocked ? <Navigate to="/" replace /> : <Outlet />}
          </div>
        </main>

        {/* ----- Téléphone : barre d'onglets ----- */}
        <nav
          className="bg-sidebar border-sidebar-border flex shrink-0 items-stretch border-t pb-[env(safe-area-inset-bottom)] lg:hidden"
          aria-label="Navigation principale"
        >
          {onglets.map((m) => {
            const Icon = m.icon;
            return (
              <NavLink
                key={m.id}
                to={m.path}
                end={m.path === '/'}
                className={({ isActive }) =>
                  cn(
                    'relative flex h-16 flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium',
                    isActive ? 'text-action font-semibold' : 'text-sidebar-muted',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive ? (
                      <span className="bg-action absolute inset-x-3 top-0 h-[3px] rounded-b" />
                    ) : null}
                    <Icon className="h-[26px] w-[26px]" strokeWidth={2} />
                    <span>{m.court ?? m.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
          <button
            type="button"
            aria-label="Ouvrir le menu"
            className="text-sidebar-muted flex h-16 flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium"
            onClick={() => {
              setMenuOuvert(true);
            }}
          >
            <Menu className="h-[26px] w-[26px]" strokeWidth={2} />
            <span>Menu</span>
          </button>
        </nav>
      </div>

      {/* ----- Téléphone : menu plein écran ----- */}
      {menuOuvert ? (
        <div className="bg-sidebar text-sidebar-foreground fixed inset-0 z-50 flex flex-col lg:hidden">
          <div className="border-sidebar-border flex h-14 shrink-0 items-center justify-between border-b px-4">
            <div className="flex items-center gap-2.5">
              <Pastille taille="sm" />
              <span className="font-display text-lg font-bold text-white">IMP TOITURE</span>
            </div>
            <button
              type="button"
              aria-label="Fermer le menu"
              className="hover:bg-sidebar-hover flex h-11 w-11 items-center justify-center rounded text-white"
              onClick={() => {
                setMenuOuvert(false);
              }}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 overflow-auto p-2">{listeMenu(true)}</nav>
          <div className="pb-[env(safe-area-inset-bottom)]">{piedUtilisateur}</div>
        </div>
      ) : null}
    </div>
  );
}
